// Form admin pra criar/editar Plano comercial — 2.5a (form único, NÃO wizard).
// - Wizard 3 passos vai vir na 2.5b.
// - Em EDIT, entitlements ficam read-only (badge explica) — também 2.5b.

import { useForm, useFieldArray, Controller, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createPlanSchema,
  editPlanFormSchema,
  type BillingCycleSchema,
  type CreatePlanDto,
  type CreatePlanInput,
  type PlanStatusSchema,
  type PlanTypeSchema,
} from '@control-center/shared';
import { Info, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { toast } from 'sonner';

import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import type { ExternalPlanEntity } from '../../lib/external-plans-api';
import { firstErrorMessage } from '../../lib/form-errors';
import type { SaasProductEntity } from '../../lib/saas-products-api';

export interface PlanFormProps {
  defaultValues?: Partial<CreatePlanInput>;
  editMode?: boolean;
  isPending?: boolean;
  saasProducts: SaasProductEntity[];
  externalPlans: ExternalPlanEntity[];
  onSubmit: (values: CreatePlanDto) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

const TYPE_OPTIONS: { value: PlanTypeSchema; label: string; hint: string }[] = [
  { value: 'single_saas', label: 'Single SaaS', hint: 'Exatamente 1 entitlement' },
  { value: 'bundle', label: 'Bundle', hint: '2+ entitlements' },
  { value: 'service_only', label: 'Service-only', hint: '0 entitlements (consultoria, impl.)' },
];

const STATUS_OPTIONS: { value: PlanStatusSchema; label: string }[] = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'oculto', label: 'Oculto' },
  { value: 'legado', label: 'Legado' },
  { value: 'descontinuado', label: 'Descontinuado' },
];

const BILLING_OPTIONS: { value: BillingCycleSchema; label: string }[] = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'pagamento_unico', label: 'Pagamento único' },
];

const EMPTY: CreatePlanInput = {
  slug: '',
  name: '',
  description: '',
  type: 'single_saas',
  monthly_price: 0,
  annual_price: null,
  setup_fee: 0,
  promo_price: null,
  billing_cycle: 'mensal',
  discount_annual_pct: null,
  trial_days: 0,
  requires_card_for_trial: false,
  blocks_repeat_trial: true,
  limits: {},
  features: {},
  checkout_highlights: [],
  status: 'ativo',
  entitlements: [],
};

export function PlanForm({
  defaultValues,
  editMode = false,
  isPending = false,
  saasProducts,
  externalPlans,
  onSubmit,
  onCancel,
  submitLabel = 'Salvar',
}: PlanFormProps): JSX.Element {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreatePlanInput>({
    // Em EDIT os entitlements são read-only (form manda [] e o PATCH descarta).
    // O schema de create exige type×N entitlements — usar ele em edit fazia a
    // validação falhar invisível e o submit "não fazer nada".
    resolver: zodResolver(editMode ? editPlanFormSchema : createPlanSchema),
    defaultValues: { ...EMPTY, ...defaultValues },
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entitlements',
  });

  useEffect(() => {
    if (defaultValues) reset({ ...EMPTY, ...defaultValues });
  }, [defaultValues, reset]);

  const watchedEntitlements = watch('entitlements');

  // Lookup de external plans por saas → array (pra dropdown dependente)
  const externalsBySaas = useMemo(() => {
    const map = new Map<string, ExternalPlanEntity[]>();
    for (const ext of externalPlans) {
      if (ext.status === 'descontinuado') continue;
      const list = map.get(ext.saasProductId) ?? [];
      list.push(ext);
      map.set(ext.saasProductId, list);
    }
    return map;
  }, [externalPlans]);

  function submit(values: CreatePlanInput): void | Promise<void> {
    const parsed = editMode
      ? (editPlanFormSchema.parse(values) as CreatePlanDto)
      : createPlanSchema.parse(values);
    return onSubmit(parsed);
  }

  // Validação falhou → SEMPRE dar feedback. Sem isso o submit morre silencioso
  // e o usuário acha que o botão tá quebrado.
  function onInvalid(errs: FieldErrors<CreatePlanInput>): void {
    const first = firstErrorMessage(errs);
    toast.error(first ?? 'Formulário tem campos inválidos — revise os destacados.');
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(submit, onInvalid)(e)}
      className="flex flex-col gap-5"
      noValidate
    >
      {/* Seção 1: dados */}
      <fieldset className="flex flex-col gap-4">
        <legend className="text-[12px] font-medium uppercase tracking-wider text-muted-3">
          Dados do plano
        </legend>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Nome" required error={errors.name?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Owl Suite Growth"
                {...register('name')}
              />
            )}
          </Field>

          <Field
            label="Slug"
            required
            hint={editMode ? 'Imutável após criação.' : 'lowercase, hífen, underscore'}
            error={errors.slug?.message}
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="owl-suite-growth"
                disabled={editMode}
                {...register('slug')}
              />
            )}
          </Field>
        </div>

        <Field label="Descrição" error={errors.description?.message}>
          {({ inputId, describedBy }) => (
            <Textarea
              id={inputId}
              aria-describedby={describedBy}
              rows={3}
              placeholder="Para clínicas pequenas..."
              {...register('description')}
            />
          )}
        </Field>

        {/* Textarea ↔ string[]: um destaque por linha. Linhas vazias são
            descartadas pelo schema no submit (rascunho com \n pendente não
            pode virar erro). */}
        <Controller
          control={control}
          name="checkout_highlights"
          render={({ field }) => (
            <Field
              label="Destaques do checkout"
              hint="Um por linha (máx. 6, 120 caracteres cada). Aparecem no resumo do checkout público. Vazio = textos padrão."
              error={errors.checkout_highlights?.message}
            >
              {({ inputId, describedBy }) => (
                <Textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  rows={4}
                  placeholder={'Agenda ilimitada\nLembretes por WhatsApp\nSuporte incluso'}
                  value={(field.value ?? []).join('\n')}
                  onChange={(e) => field.onChange(e.target.value.split('\n'))}
                  onBlur={field.onBlur}
                />
              )}
            </Field>
          )}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Tipo" required error={errors.type?.message}>
            {({ inputId, describedBy }) => (
              <Select id={inputId} aria-describedby={describedBy} {...register('type')}>
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} — {opt.hint}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Status" required error={errors.status?.message}>
            {({ inputId, describedBy }) => (
              <Select id={inputId} aria-describedby={describedBy} {...register('status')}>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </fieldset>

      {/* Seção 2: precificação */}
      <fieldset className="flex flex-col gap-4 border-t border-line pt-4">
        <legend className="text-[12px] font-medium uppercase tracking-wider text-muted-3">
          Precificação & cobrança
        </legend>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Preço mensal (R$)" required error={errors.monthly_price?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                aria-describedby={describedBy}
                {...register('monthly_price', { valueAsNumber: true })}
              />
            )}
          </Field>

          <Field
            label="Preço anual (R$)"
            hint="Opcional — preenche para oferecer plano anual."
            error={errors.annual_price?.message}
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                aria-describedby={describedBy}
                {...register('annual_price', {
                  setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
                })}
              />
            )}
          </Field>

          <Field label="Setup fee (R$)" error={errors.setup_fee?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                aria-describedby={describedBy}
                {...register('setup_fee', { valueAsNumber: true })}
              />
            )}
          </Field>

          <Field label="Preço promocional (R$)" error={errors.promo_price?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                aria-describedby={describedBy}
                {...register('promo_price', {
                  setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
                })}
              />
            )}
          </Field>

          <Field label="Ciclo de cobrança" required error={errors.billing_cycle?.message}>
            {({ inputId, describedBy }) => (
              <Select id={inputId} aria-describedby={describedBy} {...register('billing_cycle')}>
                {BILLING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Desconto anual (%)" error={errors.discount_annual_pct?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                max="100"
                aria-describedby={describedBy}
                {...register('discount_annual_pct', {
                  setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
                })}
              />
            )}
          </Field>

          <Field label="Trial (dias)" error={errors.trial_days?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                type="number"
                min="0"
                step="1"
                aria-describedby={describedBy}
                {...register('trial_days', { valueAsNumber: true })}
              />
            )}
          </Field>

          <div className="flex flex-col gap-2 pt-6">
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-line"
                {...register('requires_card_for_trial')}
              />
              <span>Trial exige cartão</span>
            </label>
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-line"
                {...register('blocks_repeat_trial')}
              />
              <span>Bloquear trial repetido (por documento)</span>
            </label>
          </div>
        </div>
      </fieldset>

      {/* Seção 3: entitlements (só visível em CREATE) */}
      {editMode ? (
        <fieldset className="flex flex-col gap-3 rounded-[10px] border border-line bg-panel-2 p-4">
          <div className="flex items-start gap-2 text-[12px] text-muted">
            <Info size={14} strokeWidth={1.8} className="mt-[2px] shrink-0" />
            <span>
              Edição de entitlements (adicionar/remover SaaS no plano) acontece via wizard de edição
              na <strong>Story 2.5b</strong>. Aqui você só altera os fields acima.
            </span>
          </div>
        </fieldset>
      ) : (
        <fieldset className="flex flex-col gap-3 border-t border-line pt-4">
          <div className="flex items-center justify-between">
            <legend className="text-[12px] font-medium uppercase tracking-wider text-muted-3">
              Entitlements ({watchedEntitlements?.length ?? 0})
            </legend>
            <PillButton
              icon={<Plus size={12} strokeWidth={2} />}
              type="button"
              onClick={() =>
                append({
                  saas_product_id: '',
                  external_plan_id: '',
                  limits_override: {},
                  display_order: fields.length,
                })
              }
            >
              Adicionar SaaS
            </PillButton>
          </div>

          {typeof errors.entitlements?.message === 'string' && (
            <p className="text-[12px] text-red-300/80">{errors.entitlements.message}</p>
          )}

          {fields.length === 0 ? (
            <div className="rounded-[8px] border border-dashed border-line bg-panel-3/30 p-4 text-center text-[12px] text-muted">
              Adicione SaaS ao plano (single_saas: 1, bundle: 2+, service_only: 0).
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {fields.map((f, idx) => (
                <EntitlementRow
                  key={f.id}
                  index={idx}
                  control={control}
                  saasProducts={saasProducts}
                  externalsBySaas={externalsBySaas}
                  onRemove={() => remove(idx)}
                  errors={errors.entitlements?.[idx]}
                />
              ))}
            </div>
          )}
        </fieldset>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
        {onCancel && (
          <PillButton onClick={onCancel} type="button">
            Cancelar
          </PillButton>
        )}
        <PillButton solid type="submit" disabled={isPending}>
          {isPending ? 'Salvando…' : submitLabel}
        </PillButton>
      </div>
    </form>
  );
}

// =============================================================================
// EntitlementRow — uma linha do array dinâmico
// =============================================================================

interface EntitlementRowProps {
  index: number;
  control: ReturnType<typeof useForm<CreatePlanInput>>['control'];
  saasProducts: SaasProductEntity[];
  externalsBySaas: Map<string, ExternalPlanEntity[]>;
  onRemove: () => void;
  errors:
    | {
        saas_product_id?: { message?: string };
        external_plan_id?: { message?: string };
      }
    | undefined;
}

function EntitlementRow({
  index,
  control,
  saasProducts,
  externalsBySaas,
  onRemove,
  errors,
}: EntitlementRowProps): JSX.Element {
  return (
    <div className="rounded-[10px] border border-line bg-panel-2 p-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Controller
            control={control}
            name={`entitlements.${index}.saas_product_id`}
            render={({ field }) => (
              <Field label="SaaS Product" required error={errors?.saas_product_id?.message}>
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                  >
                    <option value="" disabled>
                      Selecione o SaaS…
                    </option>
                    {saasProducts
                      .filter((s) => s.status === 'ativo' || s.status === 'em_desenvolvimento')
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </Select>
                )}
              </Field>
            )}
          />

          <Controller
            control={control}
            name={`entitlements.${index}.external_plan_id`}
            render={({ field }) => {
              const saasId = control._formValues.entitlements?.[index]?.saas_product_id as
                | string
                | undefined;
              const options = saasId ? (externalsBySaas.get(saasId) ?? []) : [];
              return (
                <Field
                  label="Plano externo"
                  required
                  error={errors?.external_plan_id?.message}
                  hint={!saasId ? 'Escolha o SaaS primeiro.' : undefined}
                >
                  {({ inputId }) => (
                    <Select
                      id={inputId}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      disabled={!saasId || options.length === 0}
                    >
                      <option value="" disabled>
                        {options.length === 0 ? 'Nenhum plano disponível' : 'Selecione o plano…'}
                      </option>
                      {options.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.slug})
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              );
            }}
          />
        </div>

        <button
          type="button"
          aria-label={`Remover entitlement ${index + 1}`}
          onClick={onRemove}
          className="mt-6 flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-bad"
        >
          <Trash2 size={14} strokeWidth={1.8} />
        </button>
      </div>
    </div>
  );
}
